<template>
    <vx-table-row :class="rowClasses">
        <vx-table-row-title>
            {{ props.modelValue.name }}
        </vx-table-row-title>

        <vx-table-row-item>
            <template v-slot:label> Status: </template>
            <group-status-badge v-if="props.modelValue.status" :status="props.modelValue.status" />
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> External ID: </template>
            <span class="break-all">{{ props.modelValue.group_id }}</span>
        </vx-table-row-item>

        <vx-table-row-item>
            <template v-slot:label> Users: </template>
            <vx-icon-loading v-if="loadingUsersCount" spin></vx-icon-loading>
            <span v-else>{{ usersCount }}</span>
        </vx-table-row-item>

        <vx-table-row-buttons v-if="canManage">
            <vx-dropdown placement="right">
                <vx-button data-test="dropdown" type="button" :color="buttonColor" outline class="w-[45px]">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item data-test="remove" @click="deleteGroup()">
                        Remove from OU
                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                    </vx-dropdown-item>

                    <vx-dropdown-item v-if="onCorporate()" data-test="move-to-another-ou" @click="showMoveModal = true">
                        Move to Another OU
                        <font-awesome-icon widthAuto :icon="faFolderOpen"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button
                :href="
                    onCorporate()
                        ? `/settings/teams/${props.modelValue.team_id}/groups/${props.modelValue.group_id}`
                        : `/settings/teams/${props.modelValue.team_id}#/current-org-unit-groups/${props.modelValue.group_id}`
                "
                data-test="edit"
                :color="buttonColor"
                outline
            >
                Edit <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>

    <org-unit-move-group-to-org-unit-modal
        v-if="canManage"
        v-model:visible="showMoveModal"
        :group="props.modelValue"
        :teamId="props.modelValue.team_id"
        :sourceOrgUnitId="props.orgUnitId"
        @moved="emit('moved', $event)"
    />
</template>

<script setup>
import { faEllipsis, faFilePen, faFolderOpen, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxIconLoading,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import OrgUnitMoveGroupToOrgUnitModal from './modals/OrgUnitMoveGroupToOrgUnitModal.vue';
import { computed, ref } from 'vue';
import { groupUsersClient, orgUnitGroupsClient } from '~/services';
import { formatNumber } from '~/utils/helpers';
import { throwSwalRequestError } from '~/utils/request';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import GroupStatusBadge from '~/components/general/GroupStatusBadge.vue';

const { onCorporate, roleOnCurrentOrgUnit } = useCurrentGroup();
const canManage = Spark.isAdminOrOwner || roleOnCurrentOrgUnit == 'admin';

const props = defineProps({
    orgUnitId: {
        type: Number,
        required: true,
    },
    modelValue: {
        type: Object,
        required: true,
    },
});

const rowClasses = computed(() => {
    const status = props.modelValue.status;
    return {
        'bg-amber-50!': status === 'draft',
        'bg-rose-50!': status === 'suspended',
        'bg-slate-100!': status === 'closed',
    };
});

const statusButtonColorMap = {
    draft: 'info',
    suspended: 'danger',
    closed: 'muted-dark',
};

const buttonColor = computed(() => {
    return statusButtonColorMap[props.modelValue.status] || 'muted';
});

const usersCount = ref('0');
const loadingUsersCount = ref(true);

const emit = defineEmits(['removed', 'moved']);

const showMoveModal = ref(false);

const getUsersCount = () => {
    groupUsersClient
        .countGroupUsers(props.modelValue.team_id, props.modelValue.group_id)
        .then(({ data }) => {
            usersCount.value = formatNumber(data.count);
        })
        .finally(() => {
            loadingUsersCount.value = false;
        });
};

const deleteGroup = () =>
    VxSwal.confirmation('Remove Group from OU', 'Are you sure? There is no undo.', 'danger', () =>
        orgUnitGroupsClient
            .removeOrgUnitGroup(props.modelValue.team_id, props.orgUnitId, props.modelValue.group_id)
            .then(() => emit('removed', props.modelValue.group_id))
            .catch(throwSwalRequestError)
    );

getUsersCount();
</script>
